import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { PROCESSING_STEPS_DRAWER_ID } from "@/utils/constants";

export interface PanelContent {
  helpPanel?: {
    header: string;
    content: ReactNode;
  };
  splitPanel?: {
    header: string;
    content: ReactNode;
  };
}

export interface PanelContentContextType {
  panelContent: PanelContent;
  /**
   * The currently open right-rail drawer (Help, Processing Steps, or Clip
   * Preview), or null when none is open. Only one drawer is open at a time,
   * which keeps the Processing Steps and Clip Preview panes mutually exclusive.
   */
  activeDrawerId: string | null;
  updatePanelContent: (content: Partial<PanelContent>) => void;
  resetPanelContent: () => void;
  setActiveDrawerId: (id: string | null) => void;
  /** Open (or close, if already open) the Processing Steps drawer. */
  toggleSplitPanel: () => void;
}

const PanelContentContext = createContext<PanelContentContextType | undefined>(
  undefined
);

interface PanelContentProviderProps {
  children: ReactNode;
  initialContent?: PanelContent;
}

export const PanelContentProvider: React.FC<PanelContentProviderProps> = ({
  children,
  initialContent = {},
}) => {
  const [panelContent, setPanelContent] =
    useState<PanelContent>(initialContent);
  // Processing Steps is shown by default on the chat route.
  const [activeDrawerId, setActiveDrawerId] = useState<string | null>(
    PROCESSING_STEPS_DRAWER_ID
  );

  const updatePanelContent = useCallback((content: Partial<PanelContent>) => {
    setPanelContent((prev) => ({
      ...prev,
      ...content,
      helpPanel: content.helpPanel
        ? { ...prev.helpPanel, ...content.helpPanel }
        : prev.helpPanel,
      splitPanel: content.splitPanel
        ? { ...prev.splitPanel, ...content.splitPanel }
        : prev.splitPanel,
    }));
  }, []);

  const resetPanelContent = useCallback(() => {
    setPanelContent(initialContent);
  }, [initialContent]);

  const toggleSplitPanel = useCallback(() => {
    setActiveDrawerId((prev) =>
      prev === PROCESSING_STEPS_DRAWER_ID ? null : PROCESSING_STEPS_DRAWER_ID
    );
  }, []);

  // Memoized so the context value keeps a stable identity across provider
  // re-renders that don't change the panel content or the open drawer.
  // Without this, every consumer (including the hundreds of clip accordions
  // that read setActiveDrawerId) re-renders on any provider render.
  const value = useMemo(
    () => ({
      panelContent,
      activeDrawerId,
      updatePanelContent,
      resetPanelContent,
      setActiveDrawerId,
      toggleSplitPanel,
    }),
    [
      panelContent,
      activeDrawerId,
      updatePanelContent,
      resetPanelContent,
      toggleSplitPanel,
    ]
  );

  return (
    <PanelContentContext.Provider value={value}>
      {children}
    </PanelContentContext.Provider>
  );
};

export const usePanelContent = () => {
  const context = useContext(PanelContentContext);
  if (!context) {
    throw new Error(
      "usePanelContent must be used within a PanelContentProvider"
    );
  }
  return context;
};
